import { createSlice } from '@reduxjs/toolkit';
import { Product, ProductPrice } from '../types/product';
import { Pagination } from '../types/pagination';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  createProductApi,
  createProductPriceApi,
  getProductByIdApi,
  getProductPricesByIdApi,
  getProductsApi,
  updateProductApi,
  updateProductPriceApi,
} from '../services/products/products';
import { setAlert } from './commonSlice';
import { createQuery } from '../utils/constants';
import { Manufacturer } from '../types/manufacturer';
import { CurrencyInfo } from '../types/currency';

interface InitialState {
  products: Product[];
  pagination: Pagination | null;
  prevPage: string | null;
  nextPage: string | null;
  filter: any;
  loader: boolean;
  saveLoader: boolean;
  selectedProduct: Product | null;
  selectedProductPrices: ProductPrice[] | null;
  selectedProductPricePagination: Pagination | null;
  selectedPrice: ProductPrice | null;
  selectedProductPricesIncluded: {
    currencies: CurrencyInfo[] | null;
  };
  included: {
    currencies: CurrencyInfo[] | null;
    productManufacturers: Manufacturer[] | null;
  };
}

interface Filter {
  limit: number | null;
}

const initialState: InitialState = {
  products: [],
  pagination: null,
  prevPage: null,
  nextPage: null,
  filter: null,
  loader: false,
  saveLoader: false,
  selectedProduct: null,
  selectedProductPrices: null,
  selectedProductPricePagination: null,
  selectedPrice: null,
  included: {
    currencies: null,
    productManufacturers: null,
  },
  selectedProductPricesIncluded: {
    currencies: null,
  },
};

export const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    getProducts: (state, action: PayloadAction<any>) => {
      state.products = action.payload;
    },
    setProducts: (state, action: PayloadAction<any>) => {
      state.products = action.payload;
    },
    setPagination: (state, action: PayloadAction<Pagination>) => {
      state.pagination = action.payload;
    },
    setIncluded: (state, action: PayloadAction<any>) => {
      state.included = action.payload;
    },
    setFilter: (state, action: PayloadAction<any>) => {
      state.filter = action.payload;
    },
    setLoader: (state, action: PayloadAction<any>) => {
      state.loader = action.payload;
    },
    setSaveLoader: (state, action: PayloadAction<any>) => {
      state.saveLoader = action.payload;
    },
    setSelectedProduct: (state, action: PayloadAction<any>) => {
      state.selectedProduct = action.payload;
    },
    setSelectedProductPrices: (state, action: PayloadAction<any>) => {
      state.selectedProductPrices = action.payload;
    },
    setSelectedProductPricesIncluded: (state, action: PayloadAction<any>) => {
      state.selectedProductPricesIncluded = action.payload;
    },
    setSelectedProductPricePagination: (state, action: PayloadAction<any>) => {
      state.selectedProductPricePagination = action.payload;
    },
    setSelectedprice: (state, action: PayloadAction<any>) => {
      state.selectedPrice = action.payload;
    },
  },
});

export const {
  getProducts,
  setProducts,
  setPagination,
  setFilter,
  setLoader,
  setSaveLoader,
  setSelectedProduct,
  setIncluded,
  setSelectedProductPrices,
  setSelectedProductPricesIncluded,
  setSelectedProductPricePagination,
  setSelectedprice,
} = productSlice.actions;
export default productSlice.reducer;

export const getProductsAction = (payload: any) => async (dispatch: any, getState: any) => {
  try {
    dispatch(setLoader(true));
    const { filter } = getState().product;
    const queryParams = { ...filter, ...payload };
    const query = '?currencyId=1&' + createQuery({ filter: queryParams });

    const response = await getProductsApi(query);
    if (response.kind === 'ok') {
      const { pagination, included, data: list } = response.data;

      dispatch(setProducts(list));
      dispatch(setIncluded(included));
      dispatch(setPagination(pagination));
    }
  } catch (error) {
  } finally {
    dispatch(setLoader(false));
  }
};

export const createProductAction = (payload: any, cb?: any) => async (dispatch: any) => {
  try {
    dispatch(setSaveLoader(true));
    const result = await createProductApi(payload);
    if (result?.kind === 'ok') {
      const { data } = result.data;
      dispatch(getProductAction(data.id));
      dispatch(setAlert('Product has been created successfully', 'success'));
      cb?.(null, true);
    } else {
    }
  } catch (err) {
  } finally {
    dispatch(setSaveLoader(false));
  }
};

export const updateProductAction = (id: any, payload: any, cb?: any) => async (dispatch: any) => {
  try {
    dispatch(setSaveLoader(true));
    const result = await updateProductApi(id, payload);
    if (result?.kind === 'ok') {
      dispatch(setAlert('Product has been updated successfully', 'success'));
      cb?.(null, true);
    } else {
    }
  } catch (err) {
  } finally {
    dispatch(setSaveLoader(false));
  }
};

export const getProductAction = (id?: number | null) => async (dispatch: any, getState: any) => {
  if (id) {
    try {
      const response = await getProductByIdApi(id);
      if (response.kind === 'ok') {
        const { data } = response.data;

        dispatch(setSelectedProduct(data));
      }
    } catch (error) {
    } finally {
      dispatch(setLoader(false));
    }
  } else {
    dispatch(setSelectedProduct(null));
  }
};

export const applyFilter = (payload: any) => async (dispatch: any, getState: any) => {
  return dispatch(setFilter(payload));
};

export const getProductPricesAction = (productId: number) => async (dispatch: any, getState: any) => {
  try {
    dispatch(setLoader(true));
    const { filter } = getState().product;
    const queryParams = { ...filter };
    // const query = '?currencyId=1&' + createQuery({ filter: queryParams });
    const query = '?' + createQuery({ filter: queryParams });

    const response = await getProductPricesByIdApi(productId, query);
    if (response.kind === 'ok') {
      const { pagination, included, data: list } = response.data;

      dispatch(setSelectedProductPrices(list));
      dispatch(setSelectedProductPricesIncluded(included));
      dispatch(setSelectedProductPricePagination(pagination));
    }
  } catch (error) {
  } finally {
    dispatch(setLoader(false));
  }
};

export const createProductPriceAction = (productId: number, payload: any, cb?: any) => async (dispatch: any) => {
  try {
    dispatch(setSaveLoader(true));
    const result = await createProductPriceApi(productId, payload);
    if (result?.kind === 'ok') {
      dispatch(setAlert('Product Price has been created successfully', 'success'));
      cb?.(null, true);
    } else {
      const { error } = result.data;
      dispatch(setAlert(error.message, 'error'));
    }
  } catch (err) {
  } finally {
    dispatch(setSaveLoader(false));
  }
};

export const updateProductPriceAction = (productid: any, priceId: number, payload: any, cb?: any) => async (dispatch: any) => {
  try {
    dispatch(setSaveLoader(true));
    const result = await updateProductPriceApi(productid, priceId, payload);
    if (result?.kind === 'ok') {
      dispatch(setAlert('Product Price has been updated successfully', 'success'));
      cb?.(null, true);
    } else {
      const { error } = result.data;
      dispatch(setAlert(error.message, 'error'));
    }
  } catch (err) {
  } finally {
    dispatch(setSaveLoader(false));
  }
};

export const getProductPriceAction = (payload: ProductPrice | null) => async (dispatch: any, getState: any) => {
  dispatch(setSelectedprice(payload));
};
